import { z } from 'zod';
import { RequestBoxSchema } from '../request-boxes.js';
import { RequestCarrierBidSchema } from './carriers.js';
import { DraftRequestItemSchema } from './items.js';
import { RequestKindSchema } from './kind.js';
import { RequestCarrierState, RequestStatus } from './status.js';

/**
 * @aiPurpose slim per-carrier projection stored inside a
 *   {@link RequestReturnSnapshotPayloadSchema}. Epic #2099 / #2135.
 *   Deliberately a NARROWER shape than the wire `RequestCarrierSchema`:
 *   it reuses `RequestCarrierBidSchema` for `bids` but OMITS
 *   `insuranceQuotes` entirely. The strict object level rejects any
 *   extra key (including a leaked `insuranceQuotes`) at parse time.
 * @aiSecurity insurance quotes are financial-like data; a frozen return
 *   snapshot is presentation-only and must never embed them.
 */
export const RequestReturnSnapshotCarrierSchema = z
  .object({
    id: z.string().uuid(),
    carrierId: z.string().regex(/^TR-\d{4}$/, 'must match TR-NNNN'),
    carrierName: z.string().min(1),
    arrivalCityId: z.string().uuid().nullable(),
    state: z.nativeEnum(RequestCarrierState),
    rejectReason: z.string().nullable(),
    bids: z.array(RequestCarrierBidSchema),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type RequestReturnSnapshotCarrier = z.infer<typeof RequestReturnSnapshotCarrierSchema>;

/**
 * @aiPurpose frozen header display subset of a China request captured at
 *   the moment of return. NO financials (client tariff / payments / china
 *   payment), NO funnel links (`leadId` / `dealId`), NO `clientToken`.
 *   `status` is the SOURCE status the request was returned FROM.
 */
const RequestReturnSnapshotHeaderSchema = z
  .object({
    id: z.string().regex(/^LR-\d{5}$/, 'must match LR-NNNNN'),
    name: z.string(),
    kind: RequestKindSchema,
    transportTypeId: z.string().uuid().nullable(),
    transportTypeName: z.string().nullable(),
    routeFrom: z.string(),
    routeTo: z.string(),
    deliveryAddress: z.string().nullable(),
    pickupAddress: z.string().nullable(),
    weight: z.number().nonnegative(),
    volume: z.number().nonnegative(),
    createdAt: z.string().datetime(),
    status: z.nativeEnum(RequestStatus),
  })
  .strict();
export type RequestReturnSnapshotHeader = z.infer<typeof RequestReturnSnapshotHeaderSchema>;

/**
 * @aiPurpose strict shape of the `RequestReturnSnapshot.payload` JSON
 *   column (epic #2099 / #2135). Composed from a small header object plus
 *   the EXISTING item/box sub-schemas and the slim carrier above. Every
 *   object level is `.strict()` so an extra/forbidden key (`attachments`,
 *   `chat`, `audit`, `clientToken`, `leadId`, `dealId`, any financial key,
 *   or a carrier `insuranceQuotes`) throws on parse and rolls back the
 *   capturing transaction before any destructive carrier delete.
 * @aiSecurity @aiPii the payload embeds names/addresses and item/box
 *   `objectKey` photo references; the whole `RequestReturnSnapshot` table
 *   is `FULLY_BLOCKED` in the agent catalog (#2134). Keep this schema the
 *   single source of truth for what may be frozen.
 */
export const RequestReturnSnapshotPayloadSchema = z
  .object({
    header: RequestReturnSnapshotHeaderSchema,
    items: z.array(DraftRequestItemSchema),
    boxes: z.array(RequestBoxSchema),
    carriers: z.array(RequestReturnSnapshotCarrierSchema),
  })
  .strict();
export type RequestReturnSnapshotPayload = z.infer<typeof RequestReturnSnapshotPayloadSchema>;
